import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Archive, BellRing, Eye, FilePlus2, FileText, Send, Users } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadError } from '../../components/layout/LoadError';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/toast';
import { useConsequence } from '../../components/ui/ConsequencePreview';
import {
  useArchivePolicy,
  usePolicies,
  usePublishPolicy,
  type PolicyKind,
  type PolicyRow,
  type PolicyStatus,
} from '../../hooks';
import { ApiError, apiFetchBlob } from '../../lib/api-client';

function actionError(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const KIND: Record<PolicyKind, string> = {
  work_rule: 'ข้อบังคับเกี่ยวกับการทำงาน',
  regulation: 'ระเบียบ / คำสั่ง',
  announcement: 'ประกาศ',
};

const STATUS: Record<PolicyStatus, { label: string; cls: string }> = {
  draft: { label: 'ร่าง', cls: 'bg-slate-100 text-slate-700' },
  published: { label: 'เผยแพร่', cls: 'bg-green-100 text-green-700' },
  archived: { label: 'เก็บถาวร', cls: 'bg-gray-100 text-gray-500' },
};

function thaiDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00+07:00` : iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('th-TH', { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', year: 'numeric' });
}

function PolicyListSkeleton() {
  return (
    <div className="card p-4 space-y-4" aria-label="กำลังโหลดรายการเอกสาร">
      {[0, 1, 2].map((row) => (
        <div key={row} className="animate-pulse border-b border-gray-100 pb-4 last:border-0 last:pb-0">
          <div className="h-4 w-2/3 bg-gray-100 rounded" />
          <div className="h-3 w-1/3 bg-gray-100 rounded mt-2" />
          <div className="h-2 w-full bg-gray-100 rounded mt-3" />
        </div>
      ))}
    </div>
  );
}

export function HRPolicies() {
  const [kind, setKind] = useState<PolicyKind | ''>('');
  const [status, setStatus] = useState<PolicyStatus | ''>('');
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const { data, isLoading, error, refetch } = usePolicies({
    kind: kind || undefined,
    status: status || undefined,
  });
  const publish = usePublishPolicy();
  const archive = useArchivePolicy();
  const toast = useToast();
  const consequence = useConsequence();
  const navigate = useNavigate();

  async function openFile(row: PolicyRow) {
    setOpeningId(row.id);
    const tab = window.open('', '_blank');
    try {
      const blob = await apiFetchBlob(`/hr/policies/${encodeURIComponent(row.id)}/file`);
      const url = URL.createObjectURL(blob);
      if (tab) tab.location.href = url;
      else window.location.href = url;
      // ให้ PDF viewer โหลดจาก blob ให้เสร็จก่อนคืนหน่วยความจำ.
      window.setTimeout(() => URL.revokeObjectURL(url), 5 * 60_000);
    } catch (e) {
      tab?.close();
      toast.error(actionError(e, 'เปิดไฟล์ไม่สำเร็จ กรุณาลองใหม่'));
    } finally {
      setOpeningId(null);
    }
  }

  async function publishRow(row: PolicyRow) {
    const ok = await consequence({
      title: `เผยแพร่ ${row.doc_no} ฉบับที่ ${row.version}`,
      intro: 'พนักงานในขอบเขตจะเห็นเอกสารและเริ่มกดรับทราบได้ทันทีตามวันที่มีผล',
      lines: [
        { label: 'เอกสาร', value: row.title },
        { label: 'ผู้รับ', value: `${row.total_count} คน` },
      ],
      effects: [
        'หลังเผยแพร่จะแก้ชื่อ เนื้อหา ไฟล์ หรือขอบเขตไม่ได้',
        'หากต้องแก้ ต้องออกฉบับใหม่และพนักงานต้องรับทราบใหม่',
      ],
      confirmText: 'เผยแพร่เอกสาร',
    });
    if (!ok) return;
    setPublishingId(row.id);
    try {
      await publish.mutateAsync(row.id);
      toast.success('เผยแพร่เอกสารแล้ว');
    } catch (e) {
      toast.error(actionError(e, 'เผยแพร่เอกสารไม่สำเร็จ กรุณาลองใหม่'));
    } finally {
      setPublishingId(null);
    }
  }

  async function archiveRow(row: PolicyRow) {
    const ok = await consequence({
      title: `เก็บ ${row.doc_no} ฉบับที่ ${row.version} เข้าคลัง`,
      intro: 'พนักงานจะเปิดฉบับนี้จากรายการของตนเองไม่ได้อีก แต่หลักฐานรับทราบเดิมยังคงอยู่',
      lines: [{ label: 'เอกสาร', value: row.title }],
      effects: ['เอกสารที่เก็บถาวรแล้วนำกลับมาเผยแพร่ซ้ำไม่ได้'],
      confirmText: 'เก็บถาวร',
    });
    if (!ok) return;
    setArchivingId(row.id);
    try {
      await archive.mutateAsync(row.id);
      toast.success('เก็บเอกสารเข้าคลังแล้ว');
    } catch (e) {
      toast.error(actionError(e, 'เก็บเอกสารไม่สำเร็จ กรุณาลองใหม่'));
    } finally {
      setArchivingId(null);
    }
  }

  return (
    <AdminLayout title="ข้อบังคับและประกาศ">
      <div className="space-y-4 max-w-6xl">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[13rem]">
            <label className="label">ประเภท</label>
            <select className="input min-h-11 text-base" value={kind} onChange={(e) => setKind(e.target.value as PolicyKind | '')}>
              <option value="">ทุกประเภท</option>
              {Object.entries(KIND).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[11rem]">
            <label className="label">สถานะ</label>
            <select className="input min-h-11 text-base" value={status} onChange={(e) => setStatus(e.target.value as PolicyStatus | '')}>
              <option value="">ทุกสถานะ</option>
              <option value="draft">ร่าง</option>
              <option value="published">เผยแพร่</option>
              <option value="archived">เก็บถาวร</option>
            </select>
          </div>
          <Link to="/hr/policies/new" className="btn-cta min-h-11">
            <FilePlus2 className="w-4 h-4" /> เพิ่มเอกสาร
          </Link>
        </div>

        {isLoading ? (
          <PolicyListSkeleton />
        ) : error || !data ? (
          <LoadError error={error} onRetry={() => void refetch()} />
        ) : data.unavailable ? (
          <div className="card p-4 border-l-4 border-l-amber-400 text-sm">{data.unavailable}</div>
        ) : data.policies.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="ยังไม่มีเอกสาร"
            detail="เพิ่มข้อบังคับ ระเบียบ หรือประกาศ แล้วกำหนดพนักงานที่ต้องอ่านและรับทราบ"
            action={{ label: 'เพิ่มเอกสาร', onClick: () => navigate('/hr/policies/new') }}
          />
        ) : (
          <div className="space-y-3">
            {data.policies.map((row) => {
              const progress = row.total_count > 0 ? Math.round((row.acked_count / row.total_count) * 100) : 0;
              return (
                <article key={row.id} className="card p-4 space-y-3">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-semibold break-words">{row.title}</h2>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS[row.status].cls}`}>
                          {STATUS[row.status].label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {KIND[row.kind]} · เลขที่ {row.doc_no} · ฉบับที่ {row.version} · มีผล {thaiDate(row.effective_from)}
                      </p>
                      {row.summary && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{row.summary}</p>}
                    </div>
                    <button
                      className="btn-secondary min-h-11"
                      onClick={() => void openFile(row)}
                      disabled={openingId === row.id}
                    >
                      <Eye className="w-4 h-4" /> {openingId === row.id ? 'กำลังเปิด…' : 'เปิด PDF'}
                    </button>
                  </div>

                  {row.ack_required ? (
                    <div>
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> รับทราบแล้ว</span>
                        <span className="font-medium tabular-nums">{row.acked_count}/{row.total_count} คน</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full bg-brand-600 rounded-full" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">เอกสารนี้ไม่บังคับกดรับทราบ</p>
                  )}

                  <div className="flex gap-2 flex-wrap">
                    {row.status === 'draft' && (
                      <button className="btn-cta min-h-11" onClick={() => void publishRow(row)} disabled={publishingId === row.id}>
                        <Send className="w-4 h-4" /> {publishingId === row.id ? 'กำลังเผยแพร่…' : 'เผยแพร่'}
                      </button>
                    )}
                    {row.status === 'published' && (
                      <Link to={`/hr/policies/new?from=${encodeURIComponent(row.id)}`} className="btn-secondary min-h-11">
                        <FilePlus2 className="w-4 h-4" /> ออกฉบับใหม่
                      </Link>
                    )}
                    <Link to={`/hr/policies/${encodeURIComponent(row.id)}/acks`} className="btn-secondary min-h-11">
                      <BellRing className="w-4 h-4" /> ดูการรับทราบ
                    </Link>
                    {row.status !== 'archived' && (
                      <button
                        className="btn-secondary min-h-11"
                        onClick={() => void archiveRow(row)}
                        disabled={archivingId === row.id}
                      >
                        <Archive className="w-4 h-4" /> {archivingId === row.id ? 'กำลังเก็บ…' : 'เก็บถาวร'}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
